// src/services/challengeQuizSectionService.ts
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db, appId } from '../firebase'; // firebase.ts のパスが正しいことを確認
import { ReviewItem } from '../types'; // 必要な型をインポート
import { logger } from '../utils/logger';

const REVIEW_INTERVALS = [0, 9 * 3600 * 1000, 24 * 3600 * 1800, 54 * 3600 * 1000]; // 復習間隔を定義

/**
 * 復習アイテムを更新する関数
 * @param userId ユーザーID
 * @param answers 各問題の回答結果
 * @param currentReviewItems 現在のユーザーの復習アイテム
 */
export const updateChallengeQuiz = async (
    userId: string,
    answers: { id: string; correct: boolean; quizId: string; }[],
    currentReviewItems: ReviewItem[]
): Promise<ReviewItem[]> => {
    const newReviewItems = [...currentReviewItems];
    const now = new Date();


    logger.info("#userId:", userId);
    logger.info("#answers:", answers);
    logger.info("#currentReviewItems:", currentReviewItems);

    answers.forEach(answer => {
        let itemIndex = newReviewItems.findIndex(i => i.questionId === answer.id);
        let item: ReviewItem | null = itemIndex !== -1 ? { ...newReviewItems[itemIndex] } : null;

        if (answer.correct) {
            // 正解の場合：ステージを進める
            if (item) {
                item.stage = (item.stage || 0) + 1;

                if (item.stage < REVIEW_INTERVALS.length) {
                    item.nextReviewDate = Timestamp.fromDate(new Date(now.getTime() + REVIEW_INTERVALS[item.stage]));
                    newReviewItems[itemIndex] = item;
                } else {
                    // 復習完了（ステージを超えたら削除）
                    newReviewItems.splice(itemIndex, 1);
                }
            }
        } else {
            // 不正解の場合：ステージリセットまたは新規追加
            if (item) {
                item.stage = 0;
                item.nextReviewDate = Timestamp.fromDate(new Date(now.getTime() + REVIEW_INTERVALS[0]));
                newReviewItems[itemIndex] = item;
            } else {
                // 既に存在していない場合のみ新規追加
                if (!newReviewItems.some(i => i.questionId === answer.id)) {
                    // 登録件数が200を超えていれば古いものを削除
                    if (newReviewItems.length >= 200) {
                        newReviewItems.sort((a, b) =>
                            a.lastAnswered.toMillis() - b.lastAnswered.toMillis()
                        );
                        newReviewItems.shift(); // 最も古いものを削除
                    }

                    // 新しい復習項目を追加
                    newReviewItems.push({
                        questionId: answer.id,
                        stage: 0,
                        nextReviewDate: Timestamp.fromDate(new Date(now.getTime() + REVIEW_INTERVALS[0])),
                        lastAnswered: Timestamp.fromDate(now), // FirestoreのTimestamp型で保存
                        quizId: answer.quizId,
                    });
                }
            }
        }
    });

    try {
        const userDocRef = doc(db, `artifacts/${appId}/users/${userId}/userData`, 'ChallengeQuizSelection');

        // ★★★ 修正箇所 ★★★
        // ドキュメントが存在しない場合は新規作成、存在する場合は更新（マージ）
        await setDoc(userDocRef, {
            reviewItems: newReviewItems,
            // 必要に応じて他のフィールドもここに含めることができます
        }, { merge: true }); // merge: true が非常に重要！

        logger.log("Challenge quiz items updated/created successfully.");
        return newReviewItems; // 更新後のアイテムリストを返す
    } catch (error) {
        logger.error("Error updating challenge quiz items:", error);
        throw error;
    }
};

/**
 * 特定のクイズをユーザーのチャレンジクイズリストから削除する関数
 * (もし将来的に必要であれば、myQuizzesService の removeQuizFromUserLists と同様の機能)
 */
export const removeChallengeQuiz = async (userId: string, quizId: string) => {
    try {
        const userDocRef = doc(db, `artifacts/${appId}/users/${userId}/userData`, 'ChallengeQuizSelection');
        // ここでの`quizId`はクイズ全体のIDを想定しています。
        // もし`questionId`に基づいて特定の復習アイテムを削除したい場合は、別途ロジックが必要です。
        // 例えば、特定のクイズIDに属するすべての質問アイテムを削除するなど。
        // arrayRemove は、配列内の特定の値を削除するものです。
        // 今回は、特定のクイズに属するすべてのreviewItemsを削除するロジックは含まれていません。
        // もし必要なら、reviewItemsを読み込んでからフィルタリングして更新する必要があります。
        // 例: await updateDoc(userDocRef, { reviewItems: arrayRemove({ questionId: '...' }) });
        logger.warn("removeChallengeQuiz: この関数は現在、ChallengeQuizSelectionからの直接的なクイズID削除を実装していません。");
    } catch (error) {
        logger.error("Error removing challenge quiz:", error);
        throw error;
    }
};


/**
 * ユーザーの復習アイテムとクイズ一覧から、現在復習対象となる問題数を算出
 * @param reviewItems ユーザーの復習アイテム
 * @returns 対象件数（数値）
 */
export const calculateReviewCount = (reviewItems: ReviewItem[] = []): number => {
    const now = new Date();

    return reviewItems
        .filter(item => item.nextReviewDate && item.nextReviewDate.toDate() <= now)
        .length; // 復習対象日のものだけを数える
};

/**
 * 指定されたquestionIdを持つreviewItemsをユーザーのChallengeQuizSelection.reviewItemsのリストから削除する
 * @param userId ユーザーID
 * @param questionIdsToRemove 削除するquestionIdの配列
 * @returns 更新されたreviewItemsの配列
 */
export const removeReviewItemsByQuestionIds = async (
    userId: string,
    questionIdsToRemove: string[]
): Promise<ReviewItem[]> => {
    if (questionIdsToRemove.length === 0) {
        return []; // 削除対象がなければ何もしない
    }

    const userDocRef = doc(db, `artifacts/${appId}/users/${userId}/userData`, 'ChallengeQuizSelection');
    const docSnap = await getDoc(userDocRef);
    let currentReviewItems: ReviewItem[] = [];

    if (docSnap.exists()) {
        const data = docSnap.data();
        currentReviewItems = data.reviewItems || [];
    }

    const updatedReviewItems = currentReviewItems.filter(item =>
        !questionIdsToRemove.includes(item.questionId)
    );

    await setDoc(userDocRef, { reviewItems: updatedReviewItems }, { merge: true });
    logger.log(`Removed review items for question IDs: ${questionIdsToRemove.join(', ')}`);
    return updatedReviewItems;
};

